import { Injectable } from '@angular/core';
import { BehaviorSubject, from, iif, Observable, of as ObservableOf, of } from 'rxjs';
import {
  map, switchMap, catchError, filter, mergeMap, toArray, first, tap,
  shareReplay
} from 'rxjs/operators';

import {
  ApiProductsList,
  ApiProduct,
  ApiProductsStockList,
  ApiEmptyResponse,
  ApiProductMocacos,
  ApiProductDetailInfo,
  ApiCategoryProductsInfo,
  ApiProductsV3,
  ApiProductStock,
  ApiProductKeyType
} from 'idt-ecommerce-api/_api//api-catalog/api-products/api-products.models';
import { ApiProductsService } from 'idt-ecommerce-api/_api/api-catalog/api-products/api-products.service';
import { ApiEmpathySearch } from 'idt-ecommerce-api/_api/providers/api-empathy/api-empathy.models';
import { ApiEmpathyService } from 'idt-ecommerce-api/_api/providers/api-empathy/api-empathy.service';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { isEcommerceError } from 'idt-ecommerce-api/errors/errors.models';
// eslint-disable-next-line no-restricted-imports
import { GrowthbookConfig } from 'render/services/render-growthbook-configs/render-growthbook-configs.service';

import { Product, SearchProduct, SendProductToAFriendRequestBody } from './products.models';
import { ProductsModelsTransformer } from './products.models.transformer';


@Injectable()
export class ProductsService {

  private readonly products$ = new BehaviorSubject<Record<Product['id'], Product>>({});

  constructor(
    private readonly apiProductsService: ApiProductsService,
    private readonly apiEmpathyService: ApiEmpathyService,
    private readonly contextService: ContextService
  ) { }

  public categoryProducts(categoryId: string, includeStock?: boolean): Observable<Product[]> {
    return this.contextService.getContext$().pipe(
      filter((ctx) => ctx.store?.id != null),
      first(),
      switchMap((context: EcommerceContext) => {
        const categoryProducts$ = this.apiProductsService.categoryProducts(
          context.store.id,
          context.currentCatalogId,
          categoryId,
          context.currentLanguage.id
        );

        return this.addCategoryProductsInformation(
          categoryProducts$,
          context,
          categoryId,
          includeStock
        );
      })
    );
  }

  public searchProducts(searchTerm: string): Observable<SearchProduct[]> {
    return this.contextService.getContext$().pipe(
      filter((ctx) => ctx.store?.id != null),
      first(),
      switchMap((context: EcommerceContext) => {
        if (context.config.providers?.empathySearchUrl) {
          return this.searchProductsWithEmpathy(context, searchTerm);
        }
        return this.searchProductsWithSolr(context, searchTerm); // NOSONAR
      })
    );
  }

  public productDetailByPartNumber(
    partNumber: string,
    includeStock?: boolean,
    includeMocacos?: boolean,
    growthbookConfig?: GrowthbookConfig[]
  ): Observable<Product> {
    return this.contextService.getContext$().pipe(
      filter((ctx) => ctx.store?.id != null),
      first(),
      switchMap((context: EcommerceContext) => {
        const productDetail$ = this.apiProductsService.productDetailByPartNumber(
          context.store.id,
          context.currentCatalogId,
          partNumber,
          context.currentLanguage.id
        );

        return this.addProductInformation(
          productDetail$,
          context,
          '0',
          includeStock,
          includeMocacos,
          growthbookConfig
        );
      })
    );
  }

  public productDetail(
    productId: string,
    categoryId?: string,
    includeStock?: boolean,
    includeMocacos?: boolean,
    growthbookConfig?: GrowthbookConfig[]
  ): Observable<Product> {
    return this.contextService.getContext$().pipe(
      filter((ctx) => ctx.store?.id != null),
      first(),
      switchMap((context: EcommerceContext) => {
        const productDetail$ = this.apiProductsService.productDetail(
          context.store.id,
          context.currentCatalogId,
          categoryId || '0',
          productId,
          context.currentLanguage.id
        );

        return this.addProductInformation(
          productDetail$,
          context,
          categoryId ?? '',
          includeStock,
          includeMocacos,
          growthbookConfig
        );
      })
    );
  }

  public productDetailOrNull(
    productId: string,
    categoryId?: string,
    includeStock?: boolean,
    includeMocacos?: boolean,
    isPartNumber?: boolean,
    growthbookConfig?: GrowthbookConfig[]
  ): Observable<Product> {
    let request;
    if (isPartNumber) {
      request = this.productDetailByPartNumber(productId, includeStock, includeMocacos, growthbookConfig);
    } else {
      request = this.productDetail(productId, categoryId, includeStock, includeMocacos, growthbookConfig);
    }
    return request.pipe(
      catchError((error) => {
        if (isEcommerceError(error) && error.httpStatus === 404) {
          return ObservableOf(null);
        }
        return ObservableOf(error);
      })
    );
  }

  public sendToAFriendProduct(
    categoryId: string,
    productId: string,
    request: SendProductToAFriendRequestBody
  ): Observable<ApiEmptyResponse> {
    return this.contextService.getContext$().pipe(
      filter((ctx) => ctx.store?.id != null),
      first(),
      switchMap((context: EcommerceContext) => {
        return this.apiProductsService.sendToAFriendProduct(
          context.store.id,
          context.currentCatalogId,
          categoryId,
          productId,
          context.currentLanguage.id,
          request
        );
      })
    );
  }

  public productStock(context: EcommerceContext, products: number[]): Observable<ApiProductsStockList> {
    const productIds = products.join(',');
    return this.apiProductsService.productStock(
      context.store.id,
      context.currentCatalogId,
      productIds,
      context.currentLanguage.id
    )
      .pipe(
        map((stock: ApiProductsStockList): ApiProductsStockList => stock)
      );
  }

  public categoryProductStock(context: EcommerceContext, categoryId: string): Observable<ApiProductsStockList> {
    return this.apiProductsService.categoryProductStock(
      context.store.id,
      context.currentCatalogId,
      categoryId,
      context.currentLanguage.id
    )
      .pipe(
        map((stock: ApiProductsStockList): ApiProductsStockList => stock)
      );
  }

  public productsDetailArray(
    productIds: string,
    productsStocks?: ApiProductStock[],
    productKey?: ApiProductKeyType,
    growthbookConfig?: GrowthbookConfig[]
  ): Observable<Product[]> {
    return this.contextService.getContext$().pipe(
      filter((ctx) => ctx.store?.id != null),
      first(),
      switchMap((context: EcommerceContext) => {
        return this.apiProductsService.productsDetailArray(
          context.store.id,
          context.currentCatalogId,
          productIds,
          context.currentLanguage.id,
          productKey
        ).pipe(
          map((apiProductsList: ApiProductsList): Product[] => ProductsModelsTransformer.productsFromApi(
            apiProductsList,
            context,
            undefined,
            productsStocks,
            growthbookConfig
          )),
          tap((products) => {
            this.addProductsToStore(products);
          })
        );
      })
    );
  }

  public categoryV3Products(
    categoryId: string,
    includeStock?: boolean,
    productsAmount?: number,
    growthbookConfig?: GrowthbookConfig[]
  ): Observable<Product[]> {
    return this.contextService.getContext$().pipe(
      filter((ctx) => ctx.store?.id != null),
      first(),
      switchMap((context: EcommerceContext) => {
        const amount = productsAmount === 0 ? undefined : productsAmount;

        return this.getCategoryInfo(context, categoryId)
          .pipe(
            switchMap((categoryProducts: ApiProductsV3) => {
              if (categoryProducts.productIds.length) {
                const productIds = categoryProducts.productIds.slice(0, amount);

                if (includeStock) {
                  return this.productStock(context, productIds)
                    .pipe(
                      switchMap((productsStocks: ApiProductsStockList) => {
                        return this.productsDetailArray(
                          productIds.join(','),
                          productsStocks.stocks,
                          undefined,
                          growthbookConfig
                        );
                      })
                    );
                }

                return this.productsDetailArray(productIds.join(','), undefined, undefined, growthbookConfig);
              }

              return ObservableOf([]);
            })
          );
      })
    );
  }

  public getProducts$(): Observable<Record<string, Product>> {
    return this.products$;
  }

  public storeInformationOfProducts$(productIds: Product['id'][]): Observable<Record<string, Product>> {
    return this.products$.pipe(
      first(),
      map((storedProducts) => {
        const storedProductIds = Object.keys(storedProducts);
        return productIds.filter((id) => !storedProductIds.includes(id));
      }),
      mergeMap((targetProductIds) => iif(
        () => targetProductIds.length > 0,
        from(targetProductIds).pipe(
          mergeMap((productId: string) => this.productDetailOrNull(productId)),
          filter((product) => !!product),
          toArray(),
          tap((products: Product[]) => {
            this.addProductsToStore(products);
          })
        ),
        of([])
      )),
      switchMap(() => this.getProducts$()),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }


  public addProductsToStore(products: Product[]): void {
    const indexedProducts = products.reduce(
      (acc, product) => ({ ...acc, [product.id]: product }),
      {}
    );
    this.products$.next({
      ...this.products$.getValue(),
      ...indexedProducts
    });
  }

  private getCategoryInfo(
    context: EcommerceContext,
    categoryId: string
  ): Observable<ApiProductsV3> {
    // Para mejorar el rendimiento, nunca se devolverá el detalle de los productos en la petición /product

    return this.apiProductsService.categoryProductsV3(
      context.store.id,
      context.currentCatalogId,
      categoryId,
      context.currentLanguage.id
    );
  }

  private getProductIds(productDetail: ApiProduct): number[] {
    let productIds: number[] = [];
    const bundleProductSummaries = (productDetail?.bundleProductSummaries ?? []);
    if (['MOCACO', 'ESTILISMO', 'BUNDLE'].includes(productDetail.gridElemType!)) {
      productIds = bundleProductSummaries.map((subproduct: ApiProduct) => subproduct.id);
    } else {
      productIds = productDetail.detail.relatedProducts
        ? [productDetail.id].concat(productDetail.detail.relatedProducts.map((p) => p.id))
        : [productDetail.id];
    }
    return productIds;
  }

  private getMocacosList(context: EcommerceContext, categoryId: string, productId: string) {
    return this.apiProductsService.productMocacos(
      context.store.id,
      context.currentCatalogId,
      categoryId || '0',
      productId,
      context.currentLanguage.id
    );
  }

  private addProductInformation(
    productDetail$: Observable<ApiProduct>,
    context: EcommerceContext,
    categoryId: string,
    includeStock?: boolean,
    includeMocacos?: boolean,
    growthbookConfig?: GrowthbookConfig[]
  ): Observable<Product> {
    let productInfo$: Observable<ApiProductDetailInfo> = productDetail$.pipe(map((product) => ({ product })));
    if (includeMocacos) {
      productInfo$ = productInfo$.pipe(
        switchMap((productInfo: ApiProductDetailInfo) => {
          // Is necessary to access to the product response id if it comes from partNumber
          const productId = productInfo.product.bundleProductSummaries[0] ?? productInfo.product;
          return this.getMocacosList(context, categoryId, `${productId.id}`).pipe(
            map((mocacos: ApiProductMocacos) => ({ ...productInfo, mocacos }))
          );
        })
      );
    }

    if (includeStock) {
      productInfo$ = productInfo$.pipe(
        switchMap((productInfo: ApiProductDetailInfo) => {
          const productIds = this.getProductIds(productInfo.product);
          return this.productStock(context, productIds).pipe(
            map((stockObject: ApiProductsStockList) => ({ ...productInfo, stockObject }))
          );
        })
      );
    }

    return productInfo$.pipe(
      map((productInfo: ApiProductDetailInfo) => {
        const { product, mocacos, stockObject } = productInfo;
        return ProductsModelsTransformer.productFromApi(
          product,
          context,
          categoryId,
          stockObject?.stocks,
          true,
          mocacos?.mocacos,
          growthbookConfig
        );
      }),
      tap((product: Product) => {
        this.addProductsToStore([product]);
      })
    );
  }

  private addCategoryProductsInformation(
    categoryProducts$: Observable<ApiProductsList>,
    context: EcommerceContext,
    categoryId: string = '',
    includeStock: boolean = false
  ): Observable<Product[]> {
    let categoryProductsInfo$: Observable<ApiCategoryProductsInfo> = categoryProducts$.pipe(
      map((products) => ({ products }))
    );

    if (includeStock) {
      categoryProductsInfo$ = categoryProductsInfo$.pipe(
        switchMap((categoryProductsInfo: ApiCategoryProductsInfo) => {
          return this.categoryProductStock(context, categoryId).pipe(
            map((stockObject: ApiProductsStockList) => ({ ...categoryProductsInfo, stockObject }))
          );
        })
      );
    }

    return categoryProductsInfo$.pipe(
      map((productInfo: ApiCategoryProductsInfo) => {
        const { products, stockObject } = productInfo;
        return ProductsModelsTransformer.productsFromApi(
          products,
          context,
          categoryId,
          stockObject?.stocks
        );
      }),
      tap((products: Product[]) => {
        this.addProductsToStore(products);
      })
    );
  }

  private searchProductsWithEmpathy(context: EcommerceContext, searchTerm: string): Observable<SearchProduct[]> {
    return this.apiEmpathyService.search(
      context.store.id,
      context.currentLanguage.code,
      context.currentCatalogId,
      context.store.warehouseId.toString(),
      searchTerm
    ).pipe(
      map((searchResponse: ApiEmpathySearch): SearchProduct[] => {
        const products = ProductsModelsTransformer.productsFromApiEmpathy(searchResponse);
        return products;
      })
    );
  }

  /** @deprecated */
  private searchProductsWithSolr(context: EcommerceContext, searchTerm: string): Observable<SearchProduct[]> {
    return this.apiProductsService.searchProducts(
      context.store.id,
      context.currentCatalogId,
      searchTerm,
      '10',
      context.currentLanguage.id
    ).pipe(
      map((productList: ApiProductsList): SearchProduct[] => {
        const products = ProductsModelsTransformer.productsFromApi(productList, context);
        return products;
      })
    );
  }

}
